import swal from "sweetalert"

import { popPage } from "global-action-creators"
import { sendAction } from "shared/djangio/action-creators"
import { numberValidation } from "shared/imports/validationFunctions"
import { validateIfHasPassword } from "app/static/frontend/accounts/account-security-action-creators"

import * as actionTypes from "accounts/account-information-action-types"
import { updateInformationSettingsActionDict } from "accounts/account-action-types"
import urls from "imports/desktopPaths"
import regexes from "shared/imports/regex.js"

/**
 * Returns a function that calls the API backend to change a user's basic settings (i.e. name, username, email, permissions, email signature).
 * Refer to `update_user_information` in userdata/api for the relevant Python function.
 * @param  {Object} userDict an object with the search settings as fields
 * @return {Function}     result of sendAction
 */
export const updateGeneralInfo = (userDict) => {
    return sendAction(DjangIO.app.userdata.models.QuorumUser, updateInformationSettingsActionDict, {
        action: "update_user_information",
        kwargs: userDict,
        method: "patch",
    })
}

const cancelError = "You canceled the operation."

/**
 * The `redux-form` library calls this function when a client hits submit.
 * @param  {Object} immutableFormValues an object with the search settings as fields
 * @param  {Function} dispatch a function from Redux Forms
 * @param  {Object} props properties of the form at the time of submission
 * @return {Object}     result of dispatch(updateSearchSettings(..))
 */
export const onSubmit = async (immutableFormValues, dispatch, props) => {
    const { persona, otherPersonas, userPersona, ...formValues } = immutableFormValues.toJS()

    if (persona && persona.value) {
        const willUpdateFilters = await swal({
            buttons: ["Cancel", "Continue"],
            title: "Are you sure you want to update primary product used?",
            text: "This will overwrite all existing saved searches of the current user. Only change the persona if you wish to overwrite all of the user's saved and custom searches.",
            icon: "warning",
        })
        if (willUpdateFilters === null) {
            // user cancelled update
            throw cancelError
        }
    }

    // Update user information first so PAC Classic user creation uses the updated email
    await dispatch(
        updateGeneralInfo({
            defaultLimitedToTeams: props.defaultLimitedToTeams,
            regions: props.userRegions.map((region) => region.value),
            teamIds: props.teamsIds,
            userId: props.params.targetUserId || Userdata.id,
            ...formValues,
            // No need to send the entire Enum object to the backend
            persona: persona.value ? persona.value : persona,
            otherPersonas: otherPersonas.map((otherPersona) =>
                otherPersona.value ? otherPersona.value : otherPersona,
            ),
            userPersona: userPersona.value ? userPersona.value : userPersona,
        }),
    )

    // Create PAC Classic user after updating user information to ensure it uses the updated email
    if (formValues.canAccessPacManagement) {
        const targetUserId = props.params.targetUserId || Userdata.id
        await dispatch(matchWithPacClassicUser(targetUserId))
    }
}

/**
 * A function that is called when when form submission succeeds.
 * @param  {Object} immutableFormValues an object with the search settings as fields
 * @param  {Function} dispatch a function from Redux Forms
 * @param  {Object} props properties of the form at the time of submission
 * @return {Object}     result of dispatch(updateSearchSettings(..))
 */
export const onSubmitSuccess = (result, dispatch, props) => {
    swal({
        icon: "success",
        title: "Success!",
        text: "User information is now updated.",
    }).then(() => {
        // if we set one of the QuorumUser UserInfo fields,
        // such as can_access_pac, we need to hard reload since this
        // will affect which choices may be rendered in the SidebarItem
        // Note: The popPage action will be lost due to the reload, but the user will be redirected to the settings page
        dispatch(popPage(urls.settings))
        window.location.reload()
    })
}

export const setSubmissionStatus = (status, message) => {
    return {
        type: actionTypes.SET_SUBMISSION_STATUS,
        payload: { status, message },
    }
}

/**
 * A function that is called when when form submission fails.
 * @param  {Object} immutableFormValues an object with the search settings as fields
 * @param  {Function} dispatch a function from Redux Forms
 * @param  {Object} error error object from the failed submission
 * @return {Object}     result of dispatch(updateSearchSettings(..))
 */
export const onSubmitFail = (result, dispatch, error) => {
    if (error === cancelError) {
        return
    }

    let errorText = "Please review the form and ensure all information is complete."

    // Check for PAC Classic matching errors
    if (error?.response?.data) {
        const responseData = error.response.data
        // Use responseText if available
        if (responseData.responseText) {
            errorText = responseData.responseText
        }

        // For PAC Classic matching errors, use the specific message
        if (responseData.message) {
            errorText = responseData.message
        }

        // Check for multiple accounts error
        if (responseData.message && responseData.message.includes("Multiple PAC Classic users found")) {
            errorText = "Multiple PAC Classic accounts found with this email address. Please contact support."
        }
    }

    dispatch(setSubmissionStatus("error", errorText))

    swal({
        icon: "error",
        title: "Something went wrong.",
        text: errorText,
    })
}

export const validate = (immutableValues, props) => {
    let errors = {}
    const values = immutableValues
    if (!values.get("firstName")) {
        errors.firstName = "Please confirm your first name."
    }

    if (!values.get("lastName")) {
        errors.lastName = "Please confirm your last name."
    }

    if (!regexes.emailValidationRegex.test(values.get("emailAddress"))) {
        errors.emailAddress = "Please make sure you have entered a valid email address."
    }

    const userName = values.get("userName")

    if (!userName) {
        errors.userName = "Please input your desired username."
    }

    if (!numberValidation(values.get("bulkActionLimit"))) {
        errors.bulkActionLimit = "Please input a valid whole number."
    }

    if (!values.get("userPersona")) {
        errors.userPersona = "Please select your role."
    }

    errors = { ...errors, ...validateIfHasPassword(immutableValues, props, "the user's") }

    return errors
}

// Create action types object for sendAction
const matchWithPacClassicUserActionTypes = {
    start: actionTypes.MATCH_WITH_PAC_CLASSIC_USER_START,
    success: actionTypes.MATCH_WITH_PAC_CLASSIC_USER_SUCCESS,
    fail: actionTypes.MATCH_WITH_PAC_CLASSIC_USER_FAILURE,
}

export const matchWithPacClassicUser =
    (userId = null) =>
    (dispatch) => {
        const kwargs = userId ? { user_id: userId } : {}

        return dispatch(
            sendAction(DjangIO.app.userdata.models.QuorumUser, matchWithPacClassicUserActionTypes, {
                action: "create_pac_classic_user_if_needed",
                method: "post",
                kwargs,
            }),
        )
            .then((response) => {
                dispatch(setSubmissionStatus("", ""))

                return response
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message
                dispatch(setSubmissionStatus("error", errorMessage))

                throw error
            })
    }
